import { gsap } from '../lib/gsap/gsap.min'

export default class extends HTMLElement {
  connectedCallback () {
    this.bindResizeListener()
    if (this.isDesktopEnvironment()) {
      this.initCursor()
    }
  }

  isDesktopEnvironment () {
    if (typeof window === 'undefined') return false

    // Tablet & mobile user agent check
    const isMobileUA = /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/i.test(navigator.userAgent)
    if (isMobileUA) return false

    // Screen width: strictly 1024px and wider (desktop / laptop)
    const isDesktopWidth = window.innerWidth >= 1024
    if (!isDesktopWidth) return false

    // Pointer precision: must support hover and fine pointer (mouse/trackpad, not touch)
    const hasFinePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches
    if (!hasFinePointer) return false

    return true
  }

  initCursor () {
    if (this.sphere) return

    document.body.classList.add('has-custom-cursor')

    // Main sphere container with GPU acceleration
    this.sphere = document.createElement('div')
    this.sphere.className = 'fixed top-0 left-0 pointer-events-none z-[9999]'
    this.sphere.style.width = '10px'
    this.sphere.style.height = '10px'
    this.sphere.style.willChange = 'transform'

    // The ball body — radial gradient for 3D volume
    this.ball = document.createElement('div')
    this.ball.style.width = '100%'
    this.ball.style.height = '100%'
    this.ball.style.borderRadius = '50%'
    this.ball.style.background = 'radial-gradient(circle at 32% 32%, #4affa3 0%, #01d680 45%, #009e5a 100%)'
    this.ball.style.boxShadow = `
      inset -2px -2px 4px rgba(0,0,0,0.25),
      inset 1px 1px 3px rgba(255,255,255,0.35),
      0 2px 6px rgba(0,0,0,0.2)
    `

    // Specular highlight
    this.highlight = document.createElement('div')
    this.highlight.style.position = 'absolute'
    this.highlight.style.top = '18%'
    this.highlight.style.left = '22%'
    this.highlight.style.width = '35%'
    this.highlight.style.height = '28%'
    this.highlight.style.borderRadius = '50%'
    this.highlight.style.background = 'radial-gradient(ellipse at center, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 70%)'

    this.ball.appendChild(this.highlight)
    this.sphere.appendChild(this.ball)
    this.appendChild(this.sphere)

    // GSAP hardware-accelerated direct tracking — zero latency, 1:1 mouse movement
    this.xSet = gsap.quickSetter(this.sphere, 'x', 'px')
    this.ySet = gsap.quickSetter(this.sphere, 'y', 'px')
    this.isHovering = false

    const isClickable = (el) => {
      if (!el || typeof el.closest !== 'function') return false
      const interactive = el.closest('a, button:not([disabled]), summary, input, textarea, select, label, [role="button"], [role="tab"], .cursor-pointer:not(.cursor-not-allowed), [onclick]')
      if (!interactive) return false
      if (interactive.hasAttribute('disabled') || interactive.classList.contains('cursor-not-allowed')) return false
      return true
    }

    this.checkHover = (target) => {
      const interactive = isClickable(target)
      if (interactive && !this.isHovering) {
        this.isHovering = true
        gsap.to(this.sphere, { scale: 0, duration: 0.1, overwrite: 'auto' })
      } else if (!interactive && this.isHovering) {
        this.isHovering = false
        gsap.to(this.sphere, { scale: 1, duration: 0.15, overwrite: 'auto' })
      }
    }

    this.move = (e) => {
      this.xSet(e.clientX - 5)
      this.ySet(e.clientY - 5)
    }
    document.addEventListener('mousemove', this.move)

    this.onPointerOver = (e) => {
      this.checkHover(e.target)
    }
    document.addEventListener('pointerover', this.onPointerOver)

    this.onWindowLeave = () => {
      this.isHovering = true
      gsap.to(this.sphere, { scale: 0, duration: 0.1, overwrite: 'auto' })
    }
    this.onWindowEnter = () => {
      this.isHovering = false
      gsap.to(this.sphere, { scale: 1, duration: 0.15, overwrite: 'auto' })
    }
    document.addEventListener('mouseleave', this.onWindowLeave)
    document.addEventListener('mouseenter', this.onWindowEnter)
  }

  destroyCursor () {
    document.body.classList.remove('has-custom-cursor')
    if (this.move) document.removeEventListener('mousemove', this.move)
    if (this.onPointerOver) document.removeEventListener('pointerover', this.onPointerOver)
    if (this.onWindowLeave) document.removeEventListener('mouseleave', this.onWindowLeave)
    if (this.onWindowEnter) document.removeEventListener('mouseenter', this.onWindowEnter)

    if (this.sphere) {
      this.sphere.remove()
      this.sphere = null
    }
    this.ball = null
    this.highlight = null
  }

  bindResizeListener () {
    if (this.onResize) return
    this.onResize = () => {
      if (this.isDesktopEnvironment()) {
        if (!this.sphere) {
          this.initCursor()
        }
      } else {
        this.destroyCursor()
      }
    }
    window.addEventListener('resize', this.onResize)
  }

  disconnectedCallback () {
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize)
      this.onResize = null
    }
    this.destroyCursor()
  }
}
